<template>
  <div class="no-tabs sent-geo-modal module-form">
    <form @submit.prevent="submitForm">
      <div class="row justify-content-md-center">
        <div
          v-show="gps.lock"
          class="col-md-6"
        >
          <div
            class="alert alert-danger text-center"
            role="alert"
          >
            <h4>{{ $t('modules.forms.gps_required') }} <i class="fa fa-spinner fa-spin fa-1x" /></h4>
            <br>
            <button
              class="btn btn-danger"
              type="button"
              @click="onGPS"
            >
              {{ $t('modules.forms.gps_button') }}
            </button>
          </div>
        </div>
        <div
          v-show="!gps.lock"
          class="col-md-6"
        >
          <div class="col-12">
            <h3>
              <span
                v-if="checkParams"
                class="edit"
              >{{ $t('modules.forms.list.edit_head') }}</span>{{ form.name }}
            </h3>
          </div>

          <div
            class="col-12 mb-3"
            v-if="checkParams || checkIfAdminOrBoss()"
          >
            <label
              for="created_at"
              class="form-label"
            >{{ $t('modules.forms.list.user') }}:</label>
            <VueSelect
              :options="users"
              :clearable="true"
              :reset-on-options-change="false"
              label="fullName"
              icon="bi bi-person"
              class="vue-select"
              style="border: 1px solid lightseagreen !important;"
              :value="selectedUserId"
              @updateSelect="updateUserValue"
              key="user"
            />
          </div>

          <div
            v-if="checkParams || checkIfAdminOrBoss()"
            class="col-12 mb-3"
          >
            <label
              for="created_at"
              class="form-label"
            >{{ $t('modules.forms.list.createdAt') }}:</label>
            <date-picker
              :value="createdAt"
              class="flex-grow"
              :type="'datetime'"
              :range="false"
              @emitDate="updateCreatedAt"
              style="border: 1px solid lightseagreen !important;"
              :required="true"
              :show-custom-time-buttons="true"
            />
          </div>
          <div
            v-for="(field, index) in form.fields"
            :key="index"
            class="col-12 mb-3"
          >
            <div v-if="formData[index] && field.field_type.name === 'input'">
              <label
                :for="field.name"
                class="form-label"
              >{{ field.label }}:</label>
              <input
                v-if="field.multiple === false"
                :type="field.field_type.data_type"
                :id="field.name"
                :name="field.name"
                v-model="formData[index].value"
                class="form-control"
              >
            </div>

            <div v-else-if="formData[index] && field.field_type.name === 'textarea'">
              <label
                :for="field.name"
                class="form-label"
              >{{ field.label }}:</label>
              <textarea
                :id="field.name"
                v-model="formData[index].value"
                class="form-control textarea"
              />
            </div>

            <div v-else-if="formData[index] && field.field_type.name === 'select'">
              <label
                :for="field.name"
                class="form-label"
              >{{ field.label }}</label>
              <VueSelect
                :options="getSelectOptions(field)"
                :clearable="true"
                :reset-on-options-change="false"
                @updateSelect="updateValue(field.id, $event, field.multiple)"
                label="label"
                icon="bi bi-ui-checks"
                :multiple="field.multiple"
                :value="selectFieldValues[field.id]"
                class="flex-grow vue-select"
                :key="field.name"
              />
            </div>

            <div v-else-if="formData[index] && field.field_type.name === 'radio'">
              <label
                for="field.name"
                class="form-label check-label"
              >{{ field.label }}:</label>
              <div
                v-for="option in (field.options || [])"
                :key="option.value"
                class="form-check"
              >
                <input
                  type="radio"
                  :id="`${field.name}-${option.value}`"
                  :value="option.value"
                  :name="field.name"
                  class="form-check-input"
                  v-model="formData[index].value"
                >
                <label
                  :for="`${field.name}-${option.value}`"
                  class="form-check-label"
                >
                  {{ option.label }}
                </label>
              </div>
            </div>

            <div v-else-if="formData[index] && field.field_type.name === 'checkbox'">
              <label
                v-if="field.options?.length"
                for="field.name"
                class="form-label check-label"
              >{{
                field.label
              }}:</label>
              <div
                v-for="option in (field.options || [])"
                :key="option.value"
                class="form-check"
              >
                <input
                  type="checkbox"
                  :id="`${field.name}-${option.value}`"
                  :value="option.value"
                  :name="field.name"
                  class="form-check-input"
                  v-model="formData[index].value"
                >
                <label
                  :for="`${field.name}-${option.value}`"
                  class="form-check-label"
                >
                  {{ option.label }}
                </label>
              </div>
              <div
                v-if="!field.options?.length"
                class="form-check"
              >
                <input
                  type="checkbox"
                  :id="field.name"
                  :name="field.name"
                  class="form-check-input"
                  v-model="formData[index].value"
                  :true-value="field.default_value !== '' ? field.default_value : 1"
                  :false-value="null"
                >
                <label
                  :for="field.name"
                  class="form-check-label"
                >{{ field.label }}</label>
              </div>
            </div>

            <div v-else-if="formData[index] && field.field_type.name === 'hidden'">
              <label
                v-if="this.checkParams || checkIfAdminOrBoss()"
                :for="field.name"
                class="form-label check-label"
              >{{ field.label }}:</label>
              <input
                v-if="field.multiple === false"
                :type="(this.checkParams || checkIfAdminOrBoss() ? 'text' : field.field_type.data_type)"
                :id="field.name"
                :name="field.name"
                class="form-control"
                style="border-color: lightseagreen;"
                :value="toJson(formData[index].value)"
                @input="fromJson($event, index)"
              >
            </div>

            <div v-else-if="formData[index] && field.field_type.name === 'button'">
              <button
                class="btn btn-primary"
                :value="field.default_value"
                :type="field.field_type.data_type"
              >
                {{ (params ? $t('modules.forms.list.edit') : field.label) }}
              </button>
            </div>

            <div
              v-if="v.formData?.[index]?.$errors?.length"
              v-for="error of v.formData[index].$errors"
              :key="error.$uid"
              class="vuelidate-errors"
            >
              <p class="error-msg">
                {{ error.$message }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import {getExtension, getData, getForm, saveValues, editValues, getCompanyUsers} from "@/components/content/_modules/forms/crud";
import useVuelidate from '@vuelidate/core';
import {required, minLength, maxLength, minValue, maxValue, numeric, decimal, email} from '@/vuelidate/i18n-validators';
import {Field, Form, FormDataItem, Rule, User} from "@/components/content/_modules/forms/types";
import moment from "moment";
import {checkIfAdminOrBoss, userDetails} from '@/utils/tools'
import 'vue-json-pretty/lib/styles.css';
import VueSelect from "@/components/extras/VueSelect.vue";
import DatePicker from "@/components/extras/DatePicker.vue";
import dayjs from "dayjs";

function fieldModel(module_form_field_id: number): FormDataItem {
  return {
    module_form_field_id: module_form_field_id || null,
    value: null,
  };
}

export default defineComponent({
  name: 'ModulesFormsForm',
  components: {
    DatePicker,
    VueSelect
  },
  data() {
    return {
      v: useVuelidate({$externalResults: {}}),
      moduleId: 1,
      pageTitle: this.$t('aside.menu.pages.modules.forms'),
      hash: '',
      params: this.$route.query || null,
      form: {
        id: null,
        name: '',
        fields: [],
      } as Form,
      formData: [] as FormDataItem[],
      gps: {
        lock: false,
        fieldId: null as number | null,
        data: [] as { latitude: number; longitude: number }[],
      },
      moment,
      checkParams: false,
      users: [] as User[],
      selectedUserId: null as any,
      createdAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      checkIfAdminOrBoss
    };
  },
  validations() {
    const rules: Record<number, any> = {};

    return {
      formData: this.formData.reduce((acc, field, index) => {
        const fieldRules: Record<string, any> = {};
        const formField = this.form.fields.find(f => f.id === field.module_form_field_id);

        if (formField?.rules) {
          formField.rules.forEach((rule: Rule) => {
            switch (rule.name) {
              case 'required':
                fieldRules.required = required;
                break;
              case 'minLength':
                fieldRules.minLength = minLength(rule.value);
                break;
              case 'maxLength':
                fieldRules.maxLength = maxLength(rule.value);
                break;
              case 'minValue':
                fieldRules.minValue = minValue(rule.value);
                break;
              case 'maxValue':
                fieldRules.maxValue = maxValue(rule.value);
                break;
              case 'numeric':
                fieldRules.numeric = numeric;
                break;
              case 'decimal':
                fieldRules.decimal = decimal;
                break;
              case 'email':
                fieldRules.email = email;
                break;
            }
          });
        }

        acc[index] = {value: fieldRules};
        return acc;
      }, {}),
    };
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(newQuery) {
        this.params = newQuery;
      }
    },
    'gps.data': {
      handler(newValue,oldValue) {
        if (!this.checkParams) {
          if (!newValue || (Array.isArray(newValue) && newValue.length === 0 && this.gps.fieldId === null) ) {
            this.requestGpsPermission(this.gps.fieldId);
          } else {
            this.gps.lock = false;
          }
        }

      },
      deep: true
    },
    params: {
      handler(newValue) {
        const a = false
        if(newValue?.userId && checkIfAdminOrBoss()) {
          this.getData(parseInt(newValue.userId), newValue.date)
          this.checkParams = true
        }
      },
    },
    hash: {
      handler(val) {
        this.getForm(val);
      },
      deep: true,
    },
  },
  computed: {
    selectFieldValues() {
      const values = {};
      if (!this.form.fields || !this.formData || this.formData.length === 0) {
        return values;
      }
      this.form.fields.forEach(field => {
        if (field.field_type.name === 'select') {
          const options = this.getSelectOptions(field);
          const formDataItem = this.formData.find(item => item.module_form_field_id === field.id);

          if (formDataItem && formDataItem.value !== null && formDataItem.value !== undefined) {
            if (field.multiple && Array.isArray(formDataItem.value)) {
              const primitiveValues = formDataItem.value.map(String);
              values[field.id] = options.filter(opt => primitiveValues.includes(String(opt.value)));
            } else {
              values[field.id] = options.find(opt => String(opt.value) === String(formDataItem.value));
            }
          } else {
            values[field.id] = field.multiple ? [] : null;
          }
        }
      });
      return values;
    }
  },
  methods: {
    updateUserValue(value) {
      if(value) {
        this.formData.forEach(item => {
          item.user = value.id;
        });
      }
    },
    updateCreatedAt(newValue) {
      const val = dayjs(newValue).format('YYYY-MM-DD HH:mm:ss');
      this.formData.forEach(item => {
        item.created_at = val;
      });
    },
    toJson(value) {
      return JSON.stringify(value, null, 1);
    },
    fromJson(event, index) {
      try {
        if (this.formData.length > 0){
          this.formData[index].value = JSON.parse(event.target.value);
        }
      } catch (error) {
        console.error("Błąd parsowania JSON:", error);
      }
    },
    async extensionOptions(index: number) {
      const fieldId = this.formData[index]?.module_form_field_id;
      const fieldDefaultData = this.form.fields.find(f => f.id === fieldId);

      if (!fieldDefaultData?.extensions) return;

      // Rozdziel wszystkie składniki extensions
      const defaultParts = fieldDefaultData.extensions.split(";");

      // Szukaj wartości zaczynającej się od "TO-"
      const toDirective = defaultParts.find(part => part.startsWith("TO-"));

      if (toDirective) {
        const targetDefaultValue = toDirective.substring(3); // usuń "TO-"

        // Szukaj pola o takim extensions
        const targetField = this.form.fields.find(f => f.extensions	=== targetDefaultValue);

        if (targetField) {
          const completeField = this.formData.find(f => f.module_form_field_id === targetField.id);

          if (completeField) {
            await this.getExtension(completeField, targetDefaultValue, this.formData[index].value);
          }
        }
      }
    },
    async getDefaultExtensionValue() {
      if (this.formData) {
        for (const field of this.form.fields) {
          if (field.extensions) {
            const defaultParts = field.extensions.split(";");
            const getFirstValue = defaultParts.find(part => part === "GET-LAST-VALUE");

            if (getFirstValue) {
              const completeField = this.formData.find(f => f.module_form_field_id === field.id);
              if (completeField) {
                await this.getExtension(completeField, 'GET-LAST-VALUE', field.id);
              }
            }
          }
        }
      }
    },
    getDefaultValue(field) {
      if (field.multiple && field.field_type.name !== 'radio') {
        return [];
      }
      if (field.field_type.name === 'checkbox' && field.options && field.options.length > 0) {
        return [];
      }

      if (field.field_type.name === 'checkbox' && (!field.options || field.options.length === 0)) {
        if (field.default_value !== '' && field.default_value !== undefined) {
          return field.default_value;
        }
        return false;
      }
      if (field.default_value === 'GPS' && !this.checkParams) {
        this.gps.lock = true;
        this.gps.fieldId = field.id;
        this.requestGpsPermission(field.id);
        return null;
      }

      return null;
    },
    getSelectOptions(field: Field) {
      const options = field.options || [];
      const entities = field.entity || [];
      return [
        ...options.map((option) => ({label: option.label, value: option.value})),
        ...entities.map((entity) => ({label: entity.name, value: entity.id})),
      ];
    },
    onGPS(){
      if (this.gps.fieldId) {
        this.requestGpsPermission(this.gps.fieldId);
      }
    },
    requestGpsPermission(id) {
      if(this.checkParams) {
        this.gps.lock = false
        return;
      }

      this.gps.lock = true

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
              const pos = [{
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              }];
              this.gps.data = pos
              this.updateValue(id, pos)
              this.gps.lock = false
            },
            (error) => {
              console.error(this.$t('modules.forms.gps_error'), error);
              this.$toast.error(this.$t('modules.forms.gps_required'));
            },
            {enableHighAccuracy: false, timeout: 15000, maximumAge: 0}
        );
      } else {
        this.$toast.error(this.$t('modules.forms.gps_error_browser'));
      }
    },
    getHash() {
      const {catchAll} = this.$route.params;
      const moduleName = (Array.isArray(catchAll) ? catchAll[0] : catchAll || '').toLowerCase();
      this.hash = moduleName.split('/').pop() || '';
    },
    async getExtension(target:any , to:any, value:any, options: any = null) {
      try {
        const res = await getExtension(to, value, options);
        target.value = res.data;
      } catch (e) {
        this.$toast.error('Nie można pobrać dodatku');
      }
    },
    async getData(userId: number, date: string) {
      try {
        const res = await getData(userId, date);
        this.formData = JSON.parse(JSON.stringify(res.data));
      } catch (error) {
        this.$toast.error(this.$t('modules.forms.data_get_error'));
      }
    },
    async getForm(hash: string) {
      try {
        const res = await getForm(hash);
        this.form = res.data;
      } catch (error) {
        this.$toast.error(this.$t('modules.forms.data_get_error'));
      }
    },
    async checkEdit() {
      const userId = this.checkParams
          ? this.formData[0]?.user
          : userDetails().userId;

      this.selectedUserId = this.users.find(i => i.id === userId);

      const createdAtSource = this.checkParams && this.formData[0]
          ? this.formData[0].created_at
          : dayjs().format();

      this.createdAt = dayjs(createdAtSource).format('YYYY-MM-DD HH:mm:ss');

      this.updateCreatedAt(this.createdAt);
      this.updateUserValue(this.selectedUserId);
    },
    async getCompanyUsers() {
      try {
        const res = await getCompanyUsers();
        this.users = res.data.map(user => ({
          ...user,
          fullName: `${user.name} ${user.surname}`,
        }));
      } catch (error) {
        this.$toast.error(this.$t('modules.forms.data_get_error'));
      }
    },
    editValues(data: any) {
      editValues(data, this.params)
        .then(() => {
          this.$toast.success(this.$t('modules.forms.data_save'));
          this.$router.push('/modules/forms/list');
        })
        .catch(() => {
          this.$toast.error(this.$t('modules.forms.data_get_error'));
        });
    },
    saveValues(data: any) {
      saveValues(data)
        .then(() => {
          this.$toast.success(this.$t('modules.forms.data_save'));
          this.$router.push('/modules/forms');
        })
        .catch(() => {
          this.$toast.error(this.$t('modules.forms.data_get_error'));
        });
    },
    async updateValue(fieldId: number, value: any, multiple = false) {
      const fieldIndex = this.formData.findIndex((f) => f.module_form_field_id === fieldId);
      if (fieldIndex === -1) return;

      if (multiple) {
        this.formData[fieldIndex].value = Array.isArray(value)
            ? value.map(v => (v?.value ?? v))
            : [];
      } else {
        this.formData[fieldIndex].value = value?.value ?? value ?? null;
      }

      if(!this.checkParams) {
        await this.extensionOptions(fieldIndex)
      }
    },
    submitForm() {
      this.v.$touch();
      if (this.v.$invalid) {
        return;
      }

      if (this.checkParams) {
        this.editValues(this.formData);
      } else {
        this.saveValues(this.formData);
      }

      this.v.$reset()
    },
    async initializeComponent() {
      this.getHash();
      const { userId: userIdQuery, date: dateQuery } = this.$route.query;

      const userId = Array.isArray(userIdQuery) ? userIdQuery[0] : userIdQuery;
      const date = Array.isArray(dateQuery) ? dateQuery[0] : dateQuery;

      if (userId && date && checkIfAdminOrBoss()) {
        this.checkParams = true;
        await this.getData(parseInt(userId), date);
        await this.getCompanyUsers();
        this.checkEdit();
      } else {
        await this.getForm(this.hash);
        this.formData = this.form.fields.map(field => {
          const formItem = fieldModel(field.id);
          formItem.value = this.getDefaultValue(field);
          return formItem;
        });
        await this.getDefaultExtensionValue();
        if (checkIfAdminOrBoss()) {
          await this.getCompanyUsers();
          this.checkEdit();
        }
      }
    }
  },
  mounted() {
    this.initializeComponent();
  },
  beforeMount() {
    this.$emit('pageName', this.pageTitle);
    document.title = document.title + ' ' + this.pageTitle;
    this.$emit('emitGetComponentId', this.moduleId);
  },
});
</script>

<style lang="scss">
.module-form {
  .edit{
    background: #252422;
    color: #fff;
    padding:5px;
    border-radius: 5px;
    font-size: 11px;
    font-weight: bold;
    text-transform: uppercase;
    vertical-align: middle;
    margin-right: 10px;
  }
  .form-control {
    &[type=text],
    &[type=file],
    &.textarea {
      padding: .45rem 1rem;
      border-radius: 0.275rem;
    }

    &.textarea {
      min-height: 100px;
    }
  }

  .form-label {
    &.check-label {
      border-bottom: 1px solid #E4E6EF;
      padding-bottom: 4px
    }
  }

  .form-check {
    padding: 4px 0 4px 2.25rem;

    .form-check-input {
      width: 18px;
      height: 18px;
      margin-top: 2px;
    }

    .form-check-label {
      padding: 2px 0;
    }
  }

  .is-invalid {
    border-color: #dc3545;
  }

  .invalid-feedback {
    color: #dc3545;
  }
}
</style>